import { v4 as uuidV4 } from 'uuid';
import { DataMode, DataOptions, DataResults, HTTPMethod, Params } from '../types';
import { DataAction } from './actions';
import { deepEqual } from '../tools/object';

export type DataState = {
  dataOptions?: DataOptions;
  data?: DataResults;
  dataStamp?: number;
  error?: string;
  isDataOutdated: boolean;
  dataRequestId?: string;
  isDataLoading: boolean;
  isEnrichDataLoading: boolean;
  isDataLoadingFinished: boolean;
  dataParams?: Params;
  endpoint: string;
  paramsForData?: Params;
  requestMethod: HTTPMethod;
  enrichmentError?: string;
};

export const initiateState = ({ dataOptions, ...dataStateOptions }: Partial<DataState> = {}): DataState => ({
  isDataLoading: false,
  isEnrichDataLoading: undefined,
  isDataLoadingFinished: false,
  isDataOutdated: false,
  endpoint: dataOptions?.providerInfo?.endpoint || '',
  requestMethod: dataOptions?.providerInfo?.requestMethod || HTTPMethod.GET,
  dataOptions,
  ...dataStateOptions,
});

export function reducer(state: DataState, action: DataAction): DataState {
  const { type, payload } = action;
  switch (type) {
    case 'SET_DATA_OPTIONS':
      return setDataOptions(state, payload);
    case 'SET_PARAMS_FOR_DATA':
      return setParamsForData(state, payload);
    case 'START_DATA_FETCH':
      return startFetch(state);
    case 'START_DATA_ENRICH_FETCH':
      return startEnrichFetch(state);
    case 'DATA_FETCH_FAILED':
      return failFetch(state, payload);
    case 'SET_DATA_ACTION':
      return setData(state, payload);
    case 'SET_IS_DATA_LOADING':
      return setIsDataLoading(state, payload);
    case 'SET_ENRICHMENT_ERROR':
      return setEnrichmentError(state, payload);
    default:
      return state;
  }
}

function setDataOptions(state: DataState, options: DataOptions): DataState {
  const { data, mode, providerInfo } = options;

  if (mode !== DataMode.isProvider && data !== undefined) {
    return setData(state, data);
  }

  const newState: DataState = {
    ...{},
    ...state,
    dataOptions: options,
    endpoint: providerInfo?.endpoint || '',
    requestMethod: providerInfo?.requestMethod || HTTPMethod.GET,
  };

  checkForUpdatedParams(newState, providerInfo?.providerParams);
  return newState;
}

function setParamsForData(state: DataState, params: Params): DataState {
  const updatedParams = removeEmptyParams(params);
  const newState: DataState = {
    ...{},
    ...state,
    paramsForData: updatedParams,
  };
  checkForUpdatedParams(newState, undefined, updatedParams);
  return newState;
}

function checkForUpdatedParams(state: DataState, providerParams?: Params, paramsForData?: Params) {
  if (!state.dataOptions || !state.paramsForData) {
    return;
  }

  const providerOverrides = providerParams || state.dataOptions.providerInfo?.providerParams || {};
  const paramsToUse = paramsForData || state.paramsForData;
  const candidateParams = {
    ...paramsToUse,
  };
  Object.keys(providerOverrides).forEach(key => {
    const value = providerOverrides[key];
    if (value === null) {
      delete candidateParams[key];
    } else {
      candidateParams[key] = value;
    }
  });

  if (!deepEqual(state.dataParams, candidateParams)) {
    state.isDataOutdated = true;
    state.dataParams = candidateParams;
    state.dataRequestId = uuidV4();
    state.error = undefined;
  }
}

function startFetch(state: DataState): DataState {
  return {
    ...{},
    ...state,
    isDataLoadingFinished: false,
    error: undefined,
  };
}

function startEnrichFetch(state: DataState): DataState {
  return {
    ...{},
    ...state,
    isEnrichDataLoading: true,
  };
}

function failFetch(state: DataState, error: string): DataState {
  return {
    ...{},
    ...state,
    error,
    isEnrichDataLoading: false,
    isDataLoadingFinished: true,
  };
}

function setEnrichmentError(state: DataState, payload: string): DataState {
  return {
    ...{},
    ...state,
    isEnrichDataLoading: false,
    isDataLoadingFinished: true,
    enrichmentError: payload,
  };
}

function setData(state: DataState, payload: DataResults | null): DataState {
  const requestId = Array.isArray(payload.requestId) ? payload.requestId[0] : payload.requestId;
  if (requestId !== state.dataRequestId) {
    return state;
  }

  return {
    ...{},
    ...state,
    data: payload,
    dataStamp: Date.now(),
    isEnrichDataLoading: false,
    isDataLoadingFinished: true,
    isDataOutdated: false,
    error: undefined,
  };
}

function setIsDataLoading(state: DataState, payload: boolean): DataState {
  return {
    ...{},
    ...state,
    isDataLoading: payload,
  };
}

function removeEmptyParams(params: Params) {
  const updatedParams = { ...params };
  Object.keys(params).forEach(key => {
    if (updatedParams[key] === undefined || updatedParams[key] === null || updatedParams[key].length === 0) {
      delete updatedParams[key];
    }
  });
  return updatedParams;
}
